import {cn} from '../lib/utils';
import {Separator} from '../primitives/separator';
import {UserAvatar} from '../user-avatar';

export interface CanvasFooterCompliance {
  disclosure: string;
  dealerEnabled: boolean;
  dealerName?: string | null;
  dealerLogoUrl?: string | null;
  companyName?: string | null;
  companyLogoUrl?: string | null;
}

export interface CanvasFooterAdvisor {
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
}

interface Props {
  advisor?: CanvasFooterAdvisor | null;
  compliance?: CanvasFooterCompliance | null;
  className?: string;
  hideTopSeparator?: boolean;
}

export const CanvasFooter = ({
  advisor,
  compliance,
  className,
  hideTopSeparator,
}: Props) => {
  const advisorName = advisor?.name?.trim() ?? '';
  const showAdvisor = advisorName.length > 0;

  const disclosure = compliance?.disclosure ?? '';
  const showCompanyLogo = !!compliance?.companyLogoUrl;
  const showDealerLogo =
    !!compliance?.dealerEnabled && !!compliance?.dealerLogoUrl;
  const showLogos = showCompanyLogo || showDealerLogo;
  const showDisclosure = disclosure.trim().length > 0;

  if (!showAdvisor && !showLogos && !showDisclosure) {
    return null;
  }

  return (
    <footer
      className={cn(
        'mt-12 flex flex-col gap-6 text-xs text-muted-foreground',
        className,
      )}
    >
      {!hideTopSeparator && <Separator />}
      {(showAdvisor || showLogos) && (
        <div className="flex flex-wrap items-center justify-between gap-6">
          {showAdvisor ? (
            <div className="flex items-center gap-3">
              <UserAvatar
                name={advisorName}
                url={advisor?.avatarUrl}
                size="lg"
                className="bg-muted-foreground text-card"
              />
              <div className="flex flex-col items-start">
                <span className="text-sm font-medium text-foreground">
                  {advisorName}
                </span>
                {advisor?.email && (
                  <span className="text-xxs">{advisor.email}</span>
                )}
              </div>
            </div>
          ) : (
            <div />
          )}
          {showLogos && (
            <div className="flex flex-wrap items-center justify-end gap-6">
              {showDealerLogo && (
                <img
                  src={compliance?.dealerLogoUrl ?? undefined}
                  alt={compliance?.dealerName ?? ''}
                  className="h-8 max-w-[140px] object-contain"
                />
              )}
              {showCompanyLogo && (
                <img
                  src={compliance?.companyLogoUrl ?? undefined}
                  alt={compliance?.companyName ?? ''}
                  className="h-8 max-w-[140px] object-contain"
                />
              )}
            </div>
          )}
        </div>
      )}

      {(showAdvisor || showLogos) && showDisclosure && <Separator />}

      {showDisclosure && (
        <p className="whitespace-pre-wrap leading-relaxed">{disclosure}</p>
      )}
    </footer>
  );
};
